/**
 * メンバー・種別の設定の読み書き。
 *
 * これまで画面は「いま出ているタスクの担当者・種別」を数えて出していたので、
 * タスクに出てこない人を置けず、直しても残らなかった。保存先（Firestore）から読み、
 * 書いたら読み直す。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business メンバー・種別の設定を API から読み、追加・更新・削除を画面へ提供する。
 * @graph-connects fetchSettings [calls] 設定を引く
 * @graph-connects createSetting [calls] 設定を足す
 * @graph-connects updateSetting [calls] 設定を直す
 * @graph-connects deleteSetting [calls] 設定を消す
 */
import { toMessage } from './to-message.js';
import { useCallback, useEffect, useState } from 'react';
import {
  createSetting,
  deleteSetting,
  fetchSettings,
  updateSetting,
  type SettingEntry,
} from './client.js';
import type { TeamId } from '@/tasks/task';

/** 画面へ渡すもの */
export interface SettingsState {
  /** 設定の一覧 */
  settings: readonly SettingEntry[];
  /** 読み込み中か */
  loading: boolean;
  /** 失敗したときの理由。無ければ空文字 */
  error: string;
  /**
   * 1 件足す。書けたときだけ true。
   *
   * `order` を渡さなければ一覧の末尾に置く。まとめて足すときは、呼び出し側で
   * 連番を渡すこと（渡さないと、同じ一覧を見たまま同時に足すので全部同じ順番になる）
   */
  add: (entry: { team: TeamId; name: string; color?: string; order?: number }) => Promise<boolean>;
  /** 1 件直す。書けたときだけ true */
  update: (
    id: string,
    patch: { name?: string; color?: string; email?: string },
  ) => Promise<boolean>;
  /** 1 件消す。消せたときだけ true */
  remove: (id: string) => Promise<boolean>;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 設定を読み込み、書き込みのたびに読み直す
 * @graph-connects fetchSettings [calls] 設定を引く
 */
export function useSettings(path: 'members' | 'kind-settings'): SettingsState {
  const [settings, setSettings] = useState<readonly SettingEntry[]>([]);
  const [loading, setLoading] = useState(true);
  // 読み込みと書き込みで理由を分けて持つ。読み直しの成功で「書けなかった」理由を消さない
  const [loadError, setLoadError] = useState('');
  const [writeError, setWriteError] = useState('');
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError('');

    fetchSettings(path)
      .then((loaded) => {
        if (alive) {
          setSettings(loaded);
        }
      })
      .catch((cause: unknown) => {
        if (alive) {
          setLoadError(toMessage(cause));
        }
      })
      .finally(() => {
        if (alive) {
          setLoading(false);
        }
      });

    return () => {
      alive = false;
    };
  }, [path, reloadCount]);

  const run = useCallback(async (action: () => Promise<unknown>): Promise<boolean> => {
    setWriteError('');
    try {
      await action();
      setReloadCount((count) => count + 1);
      return true;
    } catch (cause: unknown) {
      setWriteError(toMessage(cause));
      setReloadCount((count) => count + 1);
      return false;
    }
  }, []);

  return {
    settings,
    loading,
    error: writeError === '' ? loadError : writeError,
    add: (entry) =>
      run(() =>
        createSetting(path, {
          team: entry.team,
          name: entry.name,
          // 並び順は一覧の末尾に置く。並べ替えの画面はまだ無い
          order: entry.order ?? settings.length + 1,
          color: entry.color,
        }),
      ),
    update: (id, patch) => run(() => updateSetting(path, id, patch)),
    remove: (id) => run(() => deleteSetting(path, id)),
  };
}
